<template>
  <!-- A custom landing page replaces the whole built-in page, not part of it:
       a hosted document is a complete marketing page, and framing it inside
       the shell's own hero would fight it for the same screen. -->
  <CustomLanding
    v-if="customLandingUrl && !formRevealed"
    :html-url="customLandingUrl"
    :app-name="tenantName"
    :primary="primaryColour"
    :label="buttonLabel"
    :error-message="errorMessage ?? ''"
    @signin="revealForm()"
  />

  <!-- One centred card. welcomeTitle/welcomeText/tiles are all optional, so
       the composition must look deliberate with or without them. -->
  <PreAuthPage v-else class="login-page" wide card-class="login-card">
    <!-- 🔴 Two columns past 900px, one below it. The brand and what the app IS on the left; the
         way in on the right. Everything on the left is the TENANT'S own words — their lockup,
         their welcome title, their welcome text — so a white-label gets its own landing and not a
         composition of ours wearing their name. -->
    <div class="login-split">
      <div class="login-split__brand">
    <!-- ── 🔴 THE FULL LOCKUP WHEN THERE IS ONE, THE TILE WHEN THERE IS NOT ──
         Devin: *"we need the full logo."* The hero was a 56px square mark and
         then the tenant's name set beside it in the UI font — a logo and a
         caption doing the same job, and doing it twice.

         This is the one screen with room for the real thing, so a tenant that
         supplies `logoWideUrl` gets the lockup, drawn RAW at its own aspect
         (never inside the white tile, which is for a square mark). The name
         then becomes the accessible label rather than a second heading, so it
         is announced once and painted once.

         A tenant WITHOUT a lockup is unchanged — tile plus name — because a
         white-label that has given us one square mark has given us everything
         we may draw. -->
    <section class="sh-card__head login-page__hero">
      <img
        v-if="wideLogoUrl"
        class="login-hero__lockup"
        :src="wideLogoUrl"
        :alt="tenantName"
        data-testid="login-lockup"
        referrerpolicy="no-referrer"
        @error="wideLogoFailed = true"
      />
      <template v-else>
        <LogoTile :branding="branding" :alt="tenantName" :size="56" />
        <h1 class="login-hero__name">{{ tenantName }}</h1>
      </template>
      <p v-if="welcomeTitle" class="login-hero__welcome-title">{{ welcomeTitle }}</p>
      <p v-if="welcomeText" class="login-hero__welcome-text sh-card__subtitle">{{ welcomeText }}</p>
        </section>
      </div>

      <div class="login-split__form">

    <!-- `role="alert"` so a screen reader announces the failure the moment
         the card swaps back. The raw text stays in the title for whoever
         has to debug it. -->
    <p v-if="errorMessage" class="login-signin__error sh-card__error" role="alert" :title="errorMessage">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ t("login.errorGeneric") }}</span>
    </p>

    <!-- ── 🔴 THE APPLICATION'S OWN WAY IN (D53) ────────────────────────
         Devin: *"this should present a sign in username and password."* This
         card's only control used to be a button that asked the PASSPORT for a
         development token — which meant that on any real deployment there was
         no way in at all.

         A real form: one submit handler, so Enter works from either field
         without a keydown listener, and `type="email"`/`type="password"` so a
         password manager recognises the pair and a phone offers the right
         keyboard. `autocomplete` is named on both for the same reason —
         "username" and "current-password" are what a manager fills. -->
    <!-- ── 🔴 DEVELOPMENT ONLY: who do you want to be? (D53) ────────────
         Devin: *"let me just select the person to sign in and display their
         role so I can see and test their view easily."* D30 walks three
         personas through every stage precisely because they must see
         DIFFERENT screens, and typing an address for each is friction on the
         one activity that has found every real bug this week.

         It renders only when the server answered — and the server answers
         only in development (`services/auth/dev-people.ts`: the DevAuthEnabled
         secret AND the environment type, 404 otherwise). There is no flag in
         the shell to get wrong, and no build that ships a staff directory by
         accident.

         Choosing FILLS THE REAL FORM and submits the REAL sign-in, so what is
         exercised is the path everybody uses rather than a bypass beside it. -->
    <div v-if="devPeople.length" class="login-dev" data-testid="dev-people">
      <p class="login-dev__label">{{ "Development — sign in as" }}</p>
      <ul class="login-dev__list">
        <li v-for="person in devPeople" :key="person.email">
          <button
            type="button"
            class="login-dev__person"
            :disabled="busy || !person.canSignIn"
            :title="person.canSignIn ? `Sign in as ${person.displayName}` : 'This account has no password set'"
            :data-testid="`dev-person-${person.email}`"
            @click="signInAs(person)"
          >
            <span v-if="person.purpose" class="login-dev__purpose">{{ person.purpose }}</span>
            <span class="login-dev__who">
              <span class="login-dev__name">{{ person.displayName }}</span>
              <span class="login-dev__roles">{{ person.roles }}</span>
            </span>
            <span class="login-dev__platform">{{ person.platform }}</span>
          </button>
        </li>
      </ul>
    </div>

    <!-- ── 🔴 A PASSKEY IS THE BETTER DOOR, SO IT IS THE FIRST ONE (PNK-137) ──
         Nothing to type, nothing to remember, and nothing a lookalike page can
         collect: the browser binds the key to pinksy.net and refuses to offer it
         anywhere else. Drawn ABOVE the password form because the order of two
         options is itself a recommendation.

         Rendered only where the browser can actually do it — an affordance that
         appears and then explains it cannot work is worse than one never offered. -->
    <div v-if="passkeyAvailable" class="login-passkey">
      <v-btn
        type="button"
        class="login-passkey__button"
        variant="tonal"
        color="primary"
        size="large"
        block
        :loading="passkeyBusy"
        :disabled="busy"
        data-testid="login-passkey"
        @click="signInWithAPasskey()"
      >
        <v-icon start size="18">fingerprint</v-icon>
        {{ t("login.passkey") }}
      </v-btn>
      <p class="login-passkey__hint">{{ t("login.passkeyHint") }}</p>
    </div>

    <!-- 🔴 Google is offered only where it is CONFIGURED. `googleAvailable` comes from
         the server (`GET /auth/sign-in-methods` reports whether a client id is set), so
         a deployment without OAuth credentials draws no button rather than one that
         answers 500 to the person least able to do anything about it. -->
    <div v-if="googleAvailable" class="login-social">
      <v-btn
        type="button"
        variant="outlined"
        size="large"
        block
        :disabled="busy"
        :loading="googleBusy"
        data-testid="login-google"
        @click="signInWithGoogle()"
      >
        <v-icon start size="18">account_circle</v-icon>
        {{ t("login.google") }}
      </v-btn>
    </div>

    <div v-if="passkeyAvailable || googleAvailable" class="login-passkey__or"><span>or</span></div>

    <form class="login-form" @submit.prevent="signIn()">
      <label class="login-field">
        <span class="login-field__label">{{ t("login.email") }}</span>
        <input
          ref="emailInput"
          v-model="email"
          class="login-field__input"
          type="email"
          name="email"
          autocomplete="username"
          autocapitalize="none"
          spellcheck="false"
          required
          data-testid="login-email"
          :disabled="busy"
        />
      </label>

      <label class="login-field">
        <span class="login-field__label">{{ t("login.password") }}</span>
        <input
          v-model="password"
          class="login-field__input"
          type="password"
          name="password"
          autocomplete="current-password"
          required
          data-testid="login-password"
          :disabled="busy"
        />
      </label>

      <v-btn
        type="submit"
        class="login-signin__button"
        color="primary"
        size="large"
        block
        :loading="busy"
        data-testid="login-submit"
        >{{ errorMessage ? t("common.tryAgain") : buttonLabel }}</v-btn
      >

      <!-- ── 🔴 A WAY OUT OF A FAILED SIGN-IN (D122, PNK-143) ──────────────
           Devin: *"there is nothing that offers me the ability to reset my
           password… it should at least remember my email address. And say,
           would you like me to send a reset password to this email address?"*

           So the offer NAMES the address already typed. It appears after a
           failure — the moment somebody discovers they have the password
           wrong — and the plain link below is always there for somebody who
           knows before they try. Both end in the same request. -->
      <div class="login-reset">
        <p v-if="resetNotice" class="login-reset__notice" :class="{ 'login-reset__notice--sent': resetState === 'sent' }" role="status">
          {{ resetNotice }}
        </p>

        <template v-if="offerReset">
          <p class="login-reset__offer">{{ t("login.resetOffer", { email: email.trim() }) }}</p>
          <v-btn
            class="login-reset__send"
            variant="tonal"
            color="primary"
            size="small"
            block
            :loading="resetState === 'sending'"
            data-testid="login-reset-send"
            @click="askForReset()"
            >{{ t("login.resetSend") }}</v-btn
          >
        </template>

        <button v-else type="button" class="login-reset__link" data-testid="login-forgot" :disabled="resetState === 'sending'" @click="askForReset()">
          {{ t("login.forgot") }}
        </button>
      </div>
        </form>
      </div>
    </div>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useTheme } from "vuetify";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { DEV_PASSWORD, fetchDevPeople, type DevPerson } from "@/auth/devPeople";
import { requestReset } from "@/auth/resetPassword";
import { passkeysSupported } from "@/auth/passkey";
import { fetchSignInOptions, startGoogleSignIn } from "@/auth/signInMethods";
import { signInLabel } from "@/app/loginLinks";
import { DEFAULT_PRIMARY } from "@/app/branding";
import LogoTile from "@/shell/LogoTile.vue";
import CustomLanding from "@/shell/CustomLanding.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { APP_BRANDING, APP_HOME_PAGE, APP_LOGIN_PAGE, APP_NAME } from "@/app";

/** Matches `applyBranding()`'s own guard — the colour is handed to the landing
 *  document as a query param, so it must never be free text. */
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** "What's inside" hint row shows at most this many tiles — a preview, not a full nav. */

export default defineComponent({
  props: {
    /** Where Google brings the browser back. The root, or the MCP sign-in page (D133). */
    googleCallbackPath: { type: String, default: "/" },
  },
  name: "Login",
  components: { LogoTile, CustomLanding, PreAuthPage },
  setup(props) {
    const session = useSessionStore();
    const theme = useTheme();
    const { t } = useI18n();
    const errorMessage = computed(() => session.errorMessage);

    const tenantName = APP_NAME;
    const branding = APP_BRANDING;

    /**
     * 🔴 **THE LOCKUP, PICKED FOR THE THEME THE PAGE IS ACTUALLY WEARING.**
     *
     * The same rule `OrbitalLoader` uses for the mark: the dark-chrome variant on a dark theme, the
     * light one otherwise, and either falls back to the other rather than rendering nothing — one
     * variant is better than an empty hero.
     *
     * `wideLogoFailed` is the other half. `LogoTile` already falls back to an initial when its image
     * 404s; without the same guard here a tenant whose lockup URL went stale would get a broken-image
     * icon where their name used to be. On failure this returns null, and the template's `v-else`
     * renders the tile-and-name hero that every tenant without a lockup already gets.
     */
    const wideLogoFailed = ref(false);
    const wideLogoUrl = computed<string | null>(() => {
      if (wideLogoFailed.value) return null;
      return (
        (theme.global.current.value.dark
          ? branding.logoWideDarkUrl || branding.logoWideLightUrl
          : branding.logoWideLightUrl || branding.logoWideDarkUrl) || null
      );
    });
    const homePage = APP_HOME_PAGE;

    // The backend degrades a custom page with no document back to "default",
    // so mode alone is enough — no separate null check on the URL.
    const loginPage = APP_LOGIN_PAGE;
    const customLandingUrl: string | null = loginPage.mode === "custom" ? loginPage.htmlUrl : null;
    /** The manifest's own label wins; otherwise the shell's translated "Sign in". */
    const buttonLabel = computed(() => signInLabel(loginPage) ?? t("common.signIn"));
    const primaryColour = ((colour) => (colour && HEX_COLOUR.test(colour) ? colour : DEFAULT_PRIMARY))(branding.colours?.primary);

    // Only shown when it says something the app name doesn't already say. Today it says exactly the
    // app name, so the hero draws the name once and this is null — the rule, not a coincidence.
    const welcomeTitle = ((title) =>
      !title || title.toLowerCase() === tenantName.trim().toLowerCase() ? null : title)(homePage.welcomeTitle?.trim());
    const welcomeText = homePage.welcomeText;

    // Pre-auth, so there's no resolved-modules/allowedItems permission model
    // to check yet (that's post-sign-in, see AppHome.vue) — this is purely a
    // preview of what the tenant configured, names only, not a nav.
    //
    // A tile whose title IS the app name is dropped, same rule as
    // `welcomeTitle` above: a single-module app would otherwise say its own
    // name three times. When nothing survives, the whole row disappears.

    // 🔴 The credentials live here and NOWHERE else — not in the store, not on the global state, not
    // in a query string. They exist for the length of one request and go out of scope with the view.
    const email = ref("");
    const password = ref("");
    const busy = computed(() => session.status === "signing-in");

    /**
     * 🔴 A custom landing page asks for the FORM, not for a session (D53).
     *
     * A hosted marketing document cannot collect a password — it is a third-party page in a sandboxed
     * frame, and the one thing it must never be handed is somebody's credentials. So its "sign in"
     * button, and the fallback the shell draws when the document never reports ready, both do the
     * only correct thing: put the built-in card on screen.
     */
    const formRevealed = ref(false);
    function revealForm(): void {
      formRevealed.value = true;
    }

    /** The application's own sign-in (D53). `stores/session.ts` posts them once and keeps neither. */
    function signIn(): void {
      if (busy.value) return;
      void session.signInWithPassword(email.value, password.value);
    }

    // 🔴 The roster, in development only — an empty list everywhere else, which is what hides the
    // picker. Fetched on arrival, never waited on: the card is usable at once and the picker joins it
    // when the roster lands (a lost ask is made once more — `fetchDevPeople`).
    const devPeople = ref<DevPerson[]>([]);
    onMounted(() => {
      void fetchDevPeople().then((people) => {
        devPeople.value = people;
      });
    });

    /**
     * Fills the real fields and submits the real form. Visibly — the address and the dots appear —
     * so what happened is legible, and so a wrong seed password can simply be typed over.
     */
    function signInAs(person: DevPerson): void {
      if (busy.value || !person.canSignIn) return;
      email.value = person.email;
      password.value = DEV_PASSWORD;
      signIn();
    }

    // The address field takes focus on arrival — it is the first thing anybody types, and the
    // alternative is making every visitor reach for the mouse to begin.
    const emailInput = ref<HTMLInputElement | null>(null);
    onMounted(() => emailInput.value?.focus?.());

    /**
     * 🔴 **ASKING FOR A RESET LINK** (D122, PNK-143).
     *
     * `sent` is terminal on purpose. The endpoint answers identically for an address
     * nobody has, so there is nothing further to report and nothing to retry against —
     * a second button would only invite somebody to hammer an address into the rate
     * limit and learn nothing either way.
     */
    type ResetState = "idle" | "sending" | "sent" | "need-email" | "failed";
    const resetState = ref<ResetState>("idle");

    /**
     * The offer appears once a sign-in has actually FAILED and there is an address to
     * name — which is the moment Devin described. Before that the plain link carries it,
     * and once a link has been sent neither is shown: the answer is already on screen.
     */
    const offerReset = computed(() => errorMessage.value !== null && email.value.trim() !== "" && resetState.value !== "sent");

    const resetNotice = computed(() => {
      if (resetState.value === "sent") return t("login.resetSent");
      if (resetState.value === "need-email") return t("login.resetNeedEmail");
      if (resetState.value === "failed") return t("login.resetFailed");
      return null;
    });

    /**
     * 🔴 **PASSKEY SIGN-IN** (PNK-137).
     *
     * Asked once, at setup, rather than per click: whether the browser implements
     * WebAuthn does not change while somebody is looking at the screen, and the
     * button must not appear before we know.
     */
    const passkeyAvailable = passkeysSupported();

    /**
     * 🔴 **WHETHER GOOGLE IS OFFERED IS THE SERVER'S ANSWER, NOT A BUILD FLAG.**
     *
     * The same bundle serves every environment, so a compile-time constant would be
     * wrong somewhere. `GET /auth/sign-in-methods` reports whether a client id is
     * configured; until it answers, the button is simply absent — an affordance that
     * appears and then fails is worse than one that arrives a moment late.
     */
    const googleAvailable = ref(false);
    const googleBusy = ref(false);
    onMounted(async () => {
      googleAvailable.value = (await fetchSignInOptions()).google;
    });

    async function signInWithGoogle(): Promise<void> {
      if (googleBusy.value || busy.value) return;
      googleBusy.value = true;
      // On success the browser leaves for Google, so nothing after this runs.
      const went = await startGoogleSignIn(undefined, props.googleCallbackPath);
      if (!went) googleBusy.value = false;
    }
    // Both come from the store, so the button and the form agree about what is happening.
    const passkeyBusy = computed(() => session.status === "signing-in");

    // The ceremony and every status transition belong to the store, beside the password
    // path — see `signInWithPasskey` there. This is only the button.
    const signInWithAPasskey = (): void => void session.signInWithPasskey();

    async function askForReset(): Promise<void> {
      if (resetState.value === "sending") return;
      const address = email.value.trim();
      // 🔴 No address, no request — and the field is focused rather than merely complained
      // at, because "type it above" is only useful next to a cursor that is already there.
      if (address === "") {
        resetState.value = "need-email";
        emailInput.value?.focus?.();
        return;
      }

      resetState.value = "sending";
      const outcome = await requestReset(address);
      // 🔴 `sent` for anything the server actually answered. `requestReset` reports a
      // failure ONLY when the request never arrived, so this cannot become a signal
      // about the address — which is the whole design of the endpoint (auth/resetPassword.ts).
      resetState.value = outcome.ok ? "sent" : "failed";
    }

    return {
      t,
      tenantName,
      branding,
      welcomeTitle,
      welcomeText,
      errorMessage,
      signIn,
      email,
      password,
      busy,
      emailInput,
      devPeople,
      signInAs,
      formRevealed,
      revealForm,
      customLandingUrl,
      primaryColour,
      buttonLabel,
      wideLogoUrl,
      wideLogoFailed,
      passkeyAvailable,
      googleAvailable,
      googleBusy,
      signInWithGoogle,
      passkeyBusy,
      signInWithAPasskey,
      resetState,
      offerReset,
      resetNotice,
      askForReset,
    };
  },
});
</script>

<style scoped>
/* Page frame, glows, card and error surface come from src/styles/standalone.css
   (`.sh-page`, `.sh-card`) — every full-page shell view shares them. Only the
   sign-in page's own composition lives here. */

/*
 * 🔴 **ONE COLUMN, THEN TWO.** Below 900px this is the card it always was and the card keeps its
 * own padding. Past it the card goes edge to edge (standalone.css widens `.sh-card.login-card`)
 * and each column pays for its own — which is what lets the brand side carry a tint right out to
 * the card's edge instead of floating in a 28px margin.
 */
.login-split {
  display: flex;
  flex-direction: column;
}

.login-split__brand {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (min-width: 900px) {
  .login-split {
    display: grid;
    /* The form side is fixed: a sign-in form has one comfortable width and gains nothing from
       more. The brand side takes what is left, so the composition breathes on a wide screen
       rather than stretching the inputs. */
    grid-template-columns: minmax(0, 1fr) 440px;
    align-items: stretch;
    min-height: 460px;
  }

  .login-split__brand {
    padding: 44px 40px;
    /* A wash of the tenant's OWN primary, not a colour of ours — `color-mix` keeps it a tint of
       whatever they set, so a blue tenant gets a blue landing. Two stops rather than a flat fill:
       the diagonal is the same one the mark's gradient runs down. */
    background: linear-gradient(
      135deg,
      color-mix(in srgb, rgb(var(--v-theme-primary)) 12%, transparent),
      color-mix(in srgb, rgb(var(--v-theme-primary)) 3%, transparent)
    );
    border-inline-end: 1px solid var(--sh-hairline);
  }

  .login-split__form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 44px 32px;
  }

  /* Left-aligned on the wide layout: centred type in a column this tall reads as a placeholder. */
  .login-split__brand :deep(.sh-card__head),
  .login-split__brand .sh-card__head {
    align-items: flex-start;
    text-align: start;
    gap: 18px;
    margin-bottom: 0;
  }

  .login-split__brand .login-hero__lockup {
    height: 56px;
    max-width: 320px;
    margin: 0;
  }

  .login-split__brand .login-hero__welcome-text {
    font-size: 15px;
    line-height: 1.6;
    max-width: 34ch;
  }
}

/* 🔴 The lockup is drawn RAW — no tile, no card, no shadow. The brand pack ships it clean
   precisely so placement decisions stay with whoever is placing it, and a mark inside a second
   frame on a page that is already a card reads as a sticker. Capped by HEIGHT, because a lockup's
   width varies with the name in it and a row of them must sit on one baseline. */
.login-hero__lockup {
  display: block;
  height: 44px;
  width: auto;
  max-width: min(280px, 100%);
  object-fit: contain;
  margin: 0 auto;
}

.login-hero__name {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: var(--sh-text);
}
.login-hero__welcome-title {
  font-size: 13.5px;
  font-weight: 600;
  margin: -4px 0 0;
  color: var(--sh-text);
}
.login-hero__welcome-text {
  margin: 0;
}

/* Rows, not chips: a chip row wrapped to one lonely pill when a tenant had a
   single tile, which is the common case for a one-module app. */
.login-hero__hints {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 20px;
  padding: 10px 0;
  border-top: 1px solid var(--sh-hairline);
  border-bottom: 1px solid var(--sh-hairline);
}
.login-hero__hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 4px;
}
.login-hero__hint-icon {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.12);
}
.login-hero__hint-label {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--sh-text);
}

.login-signin__button {
  font-weight: 600;
}

@media (max-width: 480px) {
  .sh-card {
    padding: 22px 18px;
  }
}

/* ── The reset affordance (D122, PNK-143) ─────────────────────────────────── */
.login-reset {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  text-align: center;
}
.login-reset__offer {
  margin: 0;
  font-size: 13px;
  color: var(--sh-text-60);
  /* An address can be long and must not widen the card or run off it. */
  overflow-wrap: anywhere;
}
.login-reset__notice {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--sh-text-60);
}
.login-reset__notice--sent {
  color: rgb(var(--v-theme-primary));
}
.login-reset__link {
  align-self: center;
  padding: 2px 4px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12.5px;
  color: var(--sh-text-60);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color var(--sh-motion) ease;
}
.login-reset__link:hover:not(:disabled) {
  color: rgb(var(--v-theme-primary));
}
.login-reset__link:disabled {
  cursor: progress;
  opacity: 0.6;
}
.login-reset__send {
  font-weight: 600;
}

/* ── The passkey door (PNK-137) ───────────────────────────────────────────── */
.login-passkey {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}
.login-passkey__button {
  font-weight: 600;
}
.login-passkey__hint {
  margin: 0;
  text-align: center;
  font-size: 12px;
  color: var(--sh-text-60);
}
/* A rule with the word sitting in the gap — the two doors are alternatives, and the
   divider says so more quietly than a second heading would. */
.login-passkey__or {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 2px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-60);
}
.login-passkey__or::before,
.login-passkey__or::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(var(--v-theme-on-surface), 0.12);
}

.login-social {
  margin-bottom: 4px;
}
</style>